package com.sddamico.draftmachine.pages.pools

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.data.Sport
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.core.Badge
import com.sddamico.draftmachine.ds.core.BadgeTone
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.dmPanel
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.forms.IconButton
import com.sddamico.draftmachine.ds.motion.TransitionRole
import com.sddamico.draftmachine.ds.motion.morphKey
import com.sddamico.draftmachine.ds.motion.transitionRole
import com.sddamico.draftmachine.ds.roleH3
import com.sddamico.draftmachine.models.PlayerPoolResponse
import com.sddamico.draftmachine.models.PlayerResponse
import com.sddamico.draftmachine.util.poolMorphKey
import com.sddamico.draftmachine.util.sportLabel
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/** Phosphor glyph for a pool/player's sport; null (mixed) gets the stacked-pools glyph. */
fun sportIcon(sport: Sport?): String = when (sport) {
  Sport.NFL, Sport.NCAA_FOOTBALL -> "football"
  Sport.MLB -> "baseball"
  Sport.NBA, Sport.NCAA_BASKETBALL -> "basketball"
  Sport.NHL -> "hockey"
  Sport.GOLF -> "golf"
  Sport.MENS_TENNIS, Sport.WOMENS_TENNIS -> "tennis-ball"
  Sport.OTHER -> "shapes"
  null -> "stack"
}

/** Other-category entries are free-form labels rather than structured sports roster records. */
fun playerEntryPlaceholder(sport: Sport?): String =
  if (sport == Sport.OTHER) "Entry name" else "Player, Position, Team"

/** 1696 → "1,696" for the mono meta stamps. */
fun formatCount(n: Int): String {
  val s = n.toString()
  val sb = StringBuilder()
  s.forEachIndexed { i, c ->
    if (i > 0 && (s.length - i) % 3 == 0) sb.append(',')
    sb.append(c)
  }
  return sb.toString()
}

/** "NFL · 1,696 PLAYERS" / "MIXED · 1,866 PLAYERS · INCLUDES 2 POOLS" meta line. */
fun poolMeta(p: PlayerPoolResponse): String {
  val sport = p.sport?.let(::sportLabel) ?: "MIXED"
  val base = "$sport · ${formatCount(p.totalPlayerCount)} PLAYERS"
  return if (p.childPoolCount > 0) "$base · INCLUDES ${p.childPoolCount} POOL${if (p.childPoolCount == 1) "" else "S"}" else base
}

/**
 * The compact team label for a player row. Official imports store the full team name
 * ("Oklahoma Sooners") in [PlayerResponse.team] so pools can be built per school, but the rows
 * here are narrow — prefer the abbreviation when the catalog has one.
 */
fun playerTeamLabel(p: PlayerResponse): String? =
  p.teamShortName?.takeIf { it.isNotBlank() } ?: p.team?.takeIf { it.isNotBlank() }

/** "Player, Position, Team" line for a pool detail's player rows: "QB · KC" style meta. */
fun playerMeta(p: PlayerResponse): String =
  listOfNotNull(p.position?.takeIf { it.isNotBlank() }, playerTeamLabel(p))
    .joinToString(" · ")
    .ifBlank { sportLabel(p.sport) }

/** Shared column panel for the pool forge/editor sections. */
fun Modifier.poolPanel(): Modifier = this
  .dmPanel().padding(14.px).borderRadius(DmEffects.radiusMd)
  .displayFlex().flexDirection(FlexDirection.Column).gap(12.px)

/** Compact nested-pool row: sport glyph, name, Official badge, total count, open/remove affordances. */
@Composable
fun ChildPoolRow(child: PlayerPoolResponse, onClick: (() -> Unit)? = null, onRemove: (() -> Unit)? = null) {
  Div(
    attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px)
      .padding(topBottom = 10.px, leftRight = 12.px)
      .border(1.px, LineStyle.Solid, DmColor.border)
      .borderRadius(DmEffects.radiusSm)
      .backgroundColor(DmColor.surfacePanel)
      .then(if (onClick != null) Modifier.cursor(Cursor.Pointer) else Modifier)
      .toAttrs { if (onClick != null) onClick { onClick() } }
  ) {
    Icon(sportIcon(child.sport), IconWeight.Bold, Modifier.fontSize(16.px).color(DmColor.patina300))
    SpanText(
      child.name,
      Modifier.flex(1).minWidth(0.px).fontFamily(*DmType.body).fontSize(13.px)
        .fontWeight(DmType.weightSemibold).color(DmColor.text),
    )
    if (child.isOfficial) Badge("Official", tone = BadgeTone.Brass, icon = "seal-check")
    SpanText(
      formatCount(child.totalPlayerCount),
      Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textMuted),
    )
    if (onRemove != null) {
      IconButton(
        icon = "x", label = "Remove pool", variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
        onClick = onRemove,
      )
    }
  }
}

/** Compact player row shared by the detail list and the forge's recent-adds list. */
@Composable
fun PoolPlayerRow(sport: Sport?, name: String, meta: String, onRemove: (() -> Unit)? = null) {
  Div(
    attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px)
      .padding(topBottom = 10.px, leftRight = 12.px)
      .border(1.px, LineStyle.Solid, DmColor.border)
      .borderRadius(DmEffects.radiusSm)
      .backgroundColor(DmColor.surfaceInset)
      .toAttrs()
  ) {
    Icon(sportIcon(sport), IconWeight.Bold, Modifier.fontSize(16.px).color(DmColor.patina300))
    SpanText(
      name,
      Modifier.flex(1).minWidth(0.px).fontFamily(*DmType.body).fontSize(13.px)
        .fontWeight(DmType.weightSemibold).color(DmColor.text),
    )
    SpanText(meta, Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textMuted))
    if (onRemove != null) {
      IconButton(
        icon = "x", label = "Remove player", variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
        onClick = onRemove,
      )
    }
  }
}

/**
 * A pools-index list row (frames 1a–1b): sport glyph plate, name + mono meta, optional [byline]
 * under the meta, [trailing] slot before the caret (Official badge / clone button).
 */
@Composable
fun PoolRow(
  pool: PlayerPoolResponse,
  onClick: () -> Unit,
  byline: (@Composable () -> Unit)? = null,
  trailing: (@Composable () -> Unit)? = null,
) {
  Div(
    attrs = Modifier.dmPanel().displayFlex().alignItems(AlignItems.Center).gap(12.px)
      .padding(topBottom = 13.px, leftRight = 14.px).borderRadius(DmEffects.radiusMd)
      .cursor(Cursor.Pointer)
      .transitionRole(TransitionRole.Item)
      .morphKey(poolMorphKey(pool.id))
      .toAttrs { onClick { onClick() } }
  ) {
    Div(
      attrs = Modifier.width(38.px).height(38.px).flex("0 0 auto")
        .displayFlex().alignItems(AlignItems.Center).justifyContent(JustifyContent.Center)
        .border(1.px, LineStyle.Solid, DmColor.borderStrong)
        .backgroundColor(DmColor.surfaceInset)
        .borderRadius(DmEffects.radiusSm)
        .toAttrs()
    ) {
      Icon(sportIcon(pool.sport), IconWeight.Bold, Modifier.fontSize(19.px).color(DmColor.patina300))
    }
    Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
      SpanText(pool.name, Modifier.roleH3().fontSize(15.px).color(DmColor.text).displayBlock())
      SpanText(
        poolMeta(pool),
        Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textMuted)
          .displayBlock().margin(top = 2.px),
      )
      byline?.invoke()
    }
    trailing?.invoke()
    Icon("caret-right", IconWeight.Bold, Modifier.fontSize(16.px).color(DmColor.steel500).flex("0 0 auto"))
  }
}
